import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, CameraOff, Check, X, Ticket, Loader2, ScanLine, LogOut, ChevronDown, ChevronUp, AlertTriangle, TrendingUp, Trophy } from "lucide-react";
import { sr, BRAND } from "../../lib/sr";
import { auth } from "../../lib/api";

const usd = (n) => `$${Number(n || 0).toLocaleString()}`;

function discountLabel(o) {
  if (!o) return "";
  if (o.discount_type === "percent") return `${o.discount_value}% off`;
  if (o.discount_type === "amount") return `${usd(o.discount_value)} off`;
  if (o.discount_type === "bogo") return "Buy one get one";
  if (o.discount_type === "free_item") return "Free item";
  return o.discount_value ? String(o.discount_value) : "Offer";
}

// Reusable scan + redeem panel used by both the in-app owner screen and Counter Mode.
export function ScanRedeemPanel({ cashierName = "" }) {
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [camError, setCamError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [cashier, setCashier] = useState(cashierName);
  const [customer, setCustomer] = useState("");
  const [amount, setAmount] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ today_total: 0, today_savings: 0 });
  const [board, setBoard] = useState({ cashiers: [], top_offer: null, total: 0, total_savings: 0 });
  const [boardRange, setBoardRange] = useState("today");
  const scannerRef = useRef(null);
  const lockRef = useRef(false);
  const audioRef = useRef(null);

  // --- feedback: success chime / error buzz + haptics ---
  const getCtx = () => {
    if (!audioRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioRef.current = new AC();
    }
    if (audioRef.current && audioRef.current.state === "suspended") { try { audioRef.current.resume(); } catch { /* noop */ } }
    return audioRef.current;
  };
  const tone = (ctx, freq, start, dur, gain = 0.16, type = "sine") => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type; osc.frequency.value = freq;
    osc.connect(g); g.connect(ctx.destination);
    const t0 = ctx.currentTime + start;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.start(t0); osc.stop(t0 + dur + 0.02);
  };
  const vibrate = (pattern) => { try { navigator.vibrate && navigator.vibrate(pattern); } catch { /* noop */ } };
  const playSuccess = () => { const c = getCtx(); if (c) { tone(c, 660, 0, 0.14); tone(c, 990, 0.12, 0.2); } vibrate([70, 40, 90]); };
  const playError = () => { const c = getCtx(); if (c) { tone(c, 200, 0, 0.32, 0.2, "square"); } vibrate([180, 70, 180]); };

  const loadFeed = () => {
    sr.offerStudio.counterActivity().then((d) => {
      setFeed(d.feed || []);
      setStats({ today_total: d.today_total || 0, today_savings: d.today_savings || 0 });
    }).catch(() => {});
    sr.offerStudio.counterLeaderboard().then((d) => setBoard(d)).catch(() => {});
  };
  useEffect(() => {
    loadFeed();
    const t = setInterval(loadFeed, 15000); // keep the live counter fresh
    return () => { clearInterval(t); stopScan(); }; /* eslint-disable-next-line */
  }, []);

  const stopScan = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (s) { try { await s.stop(); } catch { /* already stopped */ } try { await s.clear(); } catch { /* noop */ } }
  };

  const redeemValue = async (value) => {
    if (!value || lockRef.current) return;
    lockRef.current = true;
    setBusy(true);
    getCtx(); // unlock audio within the user-initiated redeem
    await stopScan();
    try {
      const body = { value, redeemed_by: cashier || undefined, customer_ref: customer || undefined };
      if (amount) body.purchase_amount = Number(amount);
      const r = await sr.offerStudio.redeemScan(body);
      if (r && r.ok === false && r.duplicate) {
        setResult({ ok: false, duplicate: true, ...r });
        playError();
        toast.warning(r.message || "Already redeemed today");
      } else {
        setResult({ ok: true, ...r });
        playSuccess();
        toast.success("Coupon redeemed");
      }
      setManual(""); setCustomer(""); setAmount("");
      loadFeed();
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Could not redeem this coupon" });
      playError();
    } finally { setBusy(false); setTimeout(() => (lockRef.current = false), 400); }
  };

  const startScan = async () => {
    setResult(null); setCamError(""); setStarting(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const h = new Html5Qrcode("qr-reader", { verbose: false });
      scannerRef.current = h;
      await h.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => { redeemValue(decoded); },
        () => { /* per-frame decode misses — ignore */ });
      setScanning(true);
    } catch (e) {
      scannerRef.current = null;
      setCamError(
        (e && (e.name === "NotAllowedError" || String(e).includes("Permission")))
          ? "Camera access was blocked. Allow camera access, or type the code below."
          : "Couldn't start the camera on this device. Type the code below instead."
      );
    } finally { setStarting(false); }
  };

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="scan-redeem-panel">
      <div className="sr-card" data-testid="scan-today-stats" style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
        <div style={{ flex: 1 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 4 }}>Redeemed today</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "var(--navy)", lineHeight: 1 }} data-testid="today-redeemed-count">{stats.today_total}</div>
        </div>
        <div style={{ width: 1, background: "var(--border)" }} />
        <div style={{ flex: 1 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 4 }}><TrendingUp size={12} /> Customer savings today</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "#067647", lineHeight: 1 }} data-testid="today-savings">{usd(stats.today_savings)}</div>
        </div>
      </div>

      {result ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 24, border: `2px solid ${result.ok ? "#067647" : (result.duplicate ? "#B54708" : "#C0362C")}`, background: result.duplicate ? "#FFFAEB" : undefined }}
          data-testid={result.ok ? "redeem-result-success" : (result.duplicate ? "redeem-result-duplicate" : "redeem-result-error")}>
          {result.ok ? (
            <>
              <div style={{ width: 56, height: 56, borderRadius: 999, background: "#E6F4EA", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Check size={30} color="#067647" /></div>
              <h2 style={{ fontSize: 22, marginTop: 12, color: "var(--navy)" }}>Redeemed</h2>
              <p style={{ fontSize: 16, marginTop: 4, color: "var(--navy)" }}><strong>{result.offer?.headline || "Coupon"}</strong></p>
              <div className="sr-badge sr-badge-green" style={{ marginTop: 8 }}><Ticket size={12} /> {discountLabel(result.offer)} · {result.offer?.code}</div>
              {typeof result.redeemed_count === "number" && result.max_redemptions ? (
                <p className="sr-muted" style={{ fontSize: 13, marginTop: 10 }}>{result.redeemed_count} of {result.max_redemptions} redeemed</p>
              ) : null}
            </>
          ) : result.duplicate ? (
            <>
              <div style={{ width: 56, height: 56, borderRadius: 999, background: "#FEF0C7", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><AlertTriangle size={30} color="#B54708" /></div>
              <h2 style={{ fontSize: 20, marginTop: 12, color: "#B54708" }}>{result.message || "Already redeemed today"}</h2>
              <p style={{ fontSize: 15, marginTop: 4, color: "var(--navy)" }}><strong>{result.offer?.headline || "Coupon"}</strong> · {result.offer?.code}</p>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>This coupon has already been used. Do not apply the discount again.</p>
            </>
          ) : (
            <>
              <div style={{ width: 56, height: 56, borderRadius: 999, background: "#FBE3E1", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><X size={30} color="#C0362C" /></div>
              <h2 style={{ fontSize: 20, marginTop: 12, color: "var(--navy)" }}>Not redeemed</h2>
              <p className="sr-muted" style={{ fontSize: 14, marginTop: 6 }}>{result.error}</p>
            </>
          )}
          <button className="sr-btn sr-btn-primary" style={{ marginTop: 18 }} onClick={() => { setResult(null); }} data-testid="scan-next-btn"><ScanLine size={16} /> Scan the next coupon</button>
        </div>
      ) : (
        <div className="sr-card" data-testid="scan-card">
          <div id="qr-reader" style={{ width: "100%", maxWidth: 380, margin: "0 auto", borderRadius: 12, overflow: "hidden", minHeight: scanning ? 300 : 0 }} />
          {!scanning && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ width: 64, height: 64, borderRadius: 999, background: "var(--blue-light)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Camera size={30} color="var(--blue)" /></div>
              <h2 style={{ fontSize: 18, marginTop: 12, color: "var(--navy)" }}>Scan a coupon QR</h2>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>Point your camera at the customer's coupon QR to redeem it instantly.</p>
              <button className="sr-btn sr-btn-primary" style={{ marginTop: 14 }} onClick={startScan} disabled={starting} data-testid="start-camera-btn">
                {starting ? <Loader2 size={16} className="sr-spin" /> : <><Camera size={16} /> Start camera</>}
              </button>
              {camError && <p style={{ fontSize: 12, marginTop: 10, color: "#C0362C" }} data-testid="cam-error">{camError}</p>}
            </div>
          )}
          {scanning && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <p className="sr-muted" style={{ fontSize: 13 }}>{busy ? "Redeeming…" : "Hold the QR steady inside the box."}</p>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 8 }} onClick={stopScan} data-testid="stop-camera-btn"><CameraOff size={14} /> Stop camera</button>
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
            <label className="sr-label">Or type the coupon code</label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input className="sr-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. SAVE20" data-testid="manual-code-input"
                onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) redeemValue(manual.trim()); }} />
              <button className="sr-btn sr-btn-primary" onClick={() => manual.trim() && redeemValue(manual.trim())} disabled={busy || !manual.trim()} data-testid="manual-redeem-btn">
                {busy ? <Loader2 size={15} className="sr-spin" /> : <><Ticket size={15} /> Redeem</>}
              </button>
            </div>
          </div>

          <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 12 }} onClick={() => setShowDetails((v) => !v)} data-testid="scan-details-toggle">
            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Optional: cashier & purchase details (for ROI)
          </button>
          {showDetails && (
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <div className="sr-field"><label className="sr-label">Cashier name</label><input className="sr-input" value={cashier} onChange={(e) => setCashier(e.target.value)} data-testid="scan-cashier-input" /></div>
              <div className="sr-field"><label className="sr-label">Customer phone/email</label><input className="sr-input" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="for per-customer rules + ROI" data-testid="scan-customer-input" /></div>
              <div className="sr-field"><label className="sr-label">Purchase amount</label><input className="sr-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="scan-amount-input" /></div>
            </div>
          )}
        </div>
      )}

      {(board.cashiers || []).length > 0 && (() => {
        const view = boardRange === "week" ? (board.week || { cashiers: [], top_offer: null, total: 0, total_savings: 0 }) : board;
        return (
        <div className="sr-card" data-testid="scan-leaderboard">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Trophy size={13} color="#B7791F" /> {boardRange === "week" ? "This week's leaderboard" : "Today's leaderboard"}</div>
            <div style={{ display: "inline-flex", gap: 6 }}>
              {[["today", "Today"], ["week", "7 days"]].map(([k, l]) => (
                <button key={k} onClick={() => setBoardRange(k)} data-testid={`leaderboard-range-${k}`}
                  className={`sr-badge ${boardRange === k ? "sr-badge-green" : "sr-badge-gray"}`} style={{ cursor: "pointer", border: "none" }}>{l}</button>
              ))}
            </div>
          </div>
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 10 }} data-testid="leaderboard-totals">{view.total} redeemed · {usd(view.total_savings)} saved{boardRange === "week" ? " over 7 days" : " today"}</div>
          {view.top_offer && (
            <div style={{ background: "#FFFAEB", border: "1px solid #FEC84B", borderRadius: 10, padding: "8px 12px", marginBottom: 10, fontSize: 13, color: "#B54708" }} data-testid="leaderboard-top-offer">
              <strong>🔥 Top offer{boardRange === "week" ? " this week" : " today"}:</strong> {view.top_offer.headline} <span style={{ opacity: .8 }}>· {view.top_offer.count}×</span>
            </div>
          )}
          {view.cashiers.length === 0 ? (
            <p className="sr-muted" style={{ fontSize: 13 }}>No redemptions in this range yet.</p>
          ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {view.cashiers.slice(0, 5).map((c, i) => (
              <div key={c.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, borderBottom: "1px solid var(--border)", paddingBottom: 6 }} data-testid={`leaderboard-row-${i}`}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800,
                    background: i === 0 ? "#FEF0C7" : "var(--surface)", color: i === 0 ? "#B7791F" : "var(--muted)" }}>{["🥇", "🥈", "🥉"][i] || (i + 1)}</span>
                  <strong style={{ color: "var(--navy)" }}>{c.name}</strong>
                  {boardRange === "week" && c.streak >= 2 && (
                    <span data-testid={`leaderboard-streak-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 800, color: "#C2410C", background: "#FFEDD5", borderRadius: 999, padding: "2px 8px" }}>🔥 {c.streak}-day</span>
                  )}
                </div>
                <div className="sr-muted" style={{ fontSize: 13 }}><strong style={{ color: "var(--navy)" }}>{c.count}</strong> redeemed{c.savings ? ` · ${usd(c.savings)} saved` : ""}</div>
              </div>
            ))}
          </div>
          )}
        </div>
        );
      })()}

      <div className="sr-card" data-testid="scan-recent">
        <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Recent in-store redemptions</div>
        {feed.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 13 }}>No in-store redemptions yet — scanned and counter redemptions show up here.</p>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {feed.slice(0, 8).map((r) => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, borderBottom: "1px solid var(--border)", paddingBottom: 6 }} data-testid={`scan-feed-${r.id}`}>
                <div><strong style={{ color: "var(--navy)" }}>{r.offer}</strong> <span className="sr-muted">· {r.code}</span></div>
                <div className="sr-muted" style={{ fontSize: 12 }}>{r.cashier}{r.amount ? ` · ${usd(r.amount)}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// In-app screen for the logged-in owner / staff.
export default function ScanRedeem() {
  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ fontSize: 28 }}>Scan &amp; Redeem</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 16 }}>Redeem a customer's coupon by scanning its QR code with your camera, or type the code.</p>
      <ScanRedeemPanel />
    </div>
  );
}

// Public Counter Mode — cashier logs in with the Workspace Code + PIN, then only sees Scan & Redeem.
export function CounterMode() {
  const navigate = useNavigate();
  const existing = auth.getUser();
  const [authed, setAuthed] = useState(existing?.role === "counter_staff" ? existing : null);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [cashier, setCashier] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const doLogin = async (e) => {
    e && e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await sr.offerStudio.counterLogin({ workspace_code: code.trim(), pin: pin.trim(), cashier: cashier.trim() });
      const user = { role: "counter_staff", name: r.cashier ? `${r.cashier} · ${r.org_name}` : `${r.org_name} Counter`, org_name: r.org_name, cashier: r.cashier };
      auth.login(r.token, user);
      setAuthed(user);
      toast.success(`Welcome — ${r.org_name} Counter`);
    } catch (e2) { setErr(e2?.response?.data?.detail || "Incorrect workspace code or PIN."); }
    finally { setBusy(false); }
  };

  const logout = () => { auth.logout(); setAuthed(null); setCode(""); setPin(""); navigate("/login"); };

  if (!authed) {
    return (
      <div className="sr-root" style={{ minHeight: "100vh", background: "var(--bg, #F6F8FB)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div className="sr-card" style={{ width: "100%", maxWidth: 380 }} data-testid="counter-login">
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ width: 56, height: 56, borderRadius: 999, background: "var(--blue-light)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ScanLine size={28} color="var(--blue)" /></div>
            <h1 style={{ fontSize: 22, marginTop: 10, color: "var(--navy)" }}>Counter Mode</h1>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{BRAND.name} · redeem coupons with your workspace PIN</p>
          </div>
          <form onSubmit={doLogin} style={{ display: "grid", gap: 10 }}>
            <div className="sr-field"><label className="sr-label">Workspace code</label><input className="sr-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. SU-0366" autoCapitalize="characters" data-testid="counter-code-input" /></div>
            <div className="sr-field"><label className="sr-label">PIN</label><input className="sr-input" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4–6 digits" data-testid="counter-pin-input" /></div>
            <div className="sr-field"><label className="sr-label">Your name (optional)</label><input className="sr-input" value={cashier} onChange={(e) => setCashier(e.target.value)} placeholder="Cashier name" data-testid="counter-cashier-input" /></div>
            {err && <p style={{ fontSize: 13, color: "#C0362C" }} data-testid="counter-login-error">{err}</p>}
            <button className="sr-btn sr-btn-primary" type="submit" disabled={busy || !code.trim() || !pin.trim()} data-testid="counter-login-btn">{busy ? <Loader2 size={15} className="sr-spin" /> : "Enter"}</button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" type="button" onClick={() => navigate("/login")} data-testid="counter-owner-login">Owner login instead</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="sr-root" style={{ minHeight: "100vh", background: "var(--bg, #F6F8FB)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#fff", borderBottom: "1px solid var(--border)", position: "sticky", top: 0, zIndex: 10 }}>
        <div><strong style={{ color: "var(--navy)" }}>{authed.org_name} · Counter</strong><div className="sr-muted" style={{ fontSize: 12 }}>Scan &amp; Redeem</div></div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={logout} data-testid="counter-logout"><LogOut size={14} /> Sign out</button>
      </div>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: 16 }}>
        <ScanRedeemPanel cashierName={authed.cashier || ""} />
      </div>
    </div>
  );
}
